import { useCallback, useMemo } from "react";
import { Device, Vector2, Vector3 } from "../models";
import { deviceToFacility, facilityToDevice } from "../helpers/geometryHelpers";
import { CameraStreamModel, IntrinsicsTranslator } from "../helpers/point-translators/intrinsicTranslator";
import { HomographyHelper } from "../helpers/point-translators/homography";

/** Width/height of the coordinate space the point translators (and stored homography/AOI points) operate in. */
const TRANSLATOR_WIDTH = 640;
const TRANSLATOR_HEIGHT = 480;

/** Ground points further than this from the device are treated as unprojectable (mirrors IntrinsicsTranslator.imageToGroundClamped). */
const MAX_GROUND_RANGE_METERS = 200;

/**
 * Camera angles (degrees) for IntrinsicsTranslator.calculateFromParameters, derived from the
 * device's calibration. Two sign conventions collide here and MUST NOT be "simplified":
 *
 * - fusionOffsetPitch/Roll hold the ABSOLUTE calibrated camera pose in radians using the
 *   firmware/FittingTool convention (NEGATIVE pitch = looking down). They are not offsets on
 *   top of the tilt — composing them with desiredTiltDegrees double-counts the tilt.
 * - IntrinsicsTranslator.calculateFromParameters expects POSITIVE pitch = looking down, so the
 *   radians->degrees conversion for pitch is -180/PI — the same conversion ProjectWithData.ts
 *   and DeviceIntrinsics use. Dropping that negation tilts the virtual camera UP and squashes
 *   every projected shape into the bottom of the frame. Roll converts with +180/PI.
 * - Uncalibrated fallback: desiredTiltDegrees is positive-down in the UI, so it passes through
 *   unnegated. Yaw (fusionOffsetYaw) is not applied — the TS translator has no yaw term.
 *
 * Guarded by intrinsicTranslatorPitchConvention.test.ts.
 */
export const intrinsicsAnglesForDevice = (device: Device): { pitchDeg: number; rollDeg: number } => {
    const radToDeg = 180 / Math.PI;
    return {
        pitchDeg: device.fusionOffsetPitch != null ? device.fusionOffsetPitch * -radToDeg : device.desiredTiltDegrees ?? 0,
        rollDeg: (device.fusionOffsetRoll ?? 0) * radToDeg,
    };
};

/**
 * intrinsicsAnglesForDevice only falls back to desiredTiltDegrees when fusionOffsetPitch is
 * null/undefined. That never happens for a real device: the firmware's DeviceSettings declares
 * FusionOffsetPitch as a non-nullable float and CoreApi stores whatever the device synced, so an
 * uncalibrated device arrives as 0 and gets projected as a perfectly level camera — horizon pinned
 * to the optical centre, ground solution ill-conditioned toward it, and long shapes flinging off
 * frame when a vertex is dragged. Synthesize the offset from the tilt here, in the one place both
 * the dashboard camera view and the webconfig offline editor go through, so the two can't diverge.
 * intrinsicsAnglesForDevice itself is left alone — its explicit "calibrated 0 means level" contract
 * is pinned by intrinsicTranslatorPitchConvention.test.ts.
 */
export const withTiltFallback = (device: Device): Device =>
    device.fusionOffsetPitch ? device : (Object.assign(Object.create(Object.getPrototypeOf(device)), device, { fusionOffsetPitch: ((device.desiredTiltDegrees ?? 0) * Math.PI) / 180 }) as Device);

export interface ImageCoordTranslator {
    /** Convert facility ground coordinates (meters) to image pixel coordinates */
    groundToImagePixels: (facilityGround: Vector3) => Vector2;
    /** Convert image pixel coordinates to facility ground coordinates */
    imagePixelsToGround: (pixel: Vector2) => Vector3;
    /** True when the device has enough calibration data to project points */
    isCalibrated: boolean;
    /** Device heading in degrees */
    deviceHeadingDeg: number;
    /** Scale ratio (pixels per meter estimate) for display */
    scaleRatio: number;
}

/**
 * Projects between facility ground coordinates (meters) and camera image pixels for a device.
 *
 * Mirrors the firmware's PointTranslatorFactory: devices with manually calibrated homography
 * points use the homography transform; everything else uses the intrinsics translator built
 * from the device's tilt/fusion offsets.
 *
 * imageWidth/imageHeight are the pixel dimensions of the image being drawn over (the
 * translators internally work at 640x480 and are scaled to the display image).
 *
 * cameraModel selects which stream the pixels come from (raw 2716N vs an Orin-dewarped
 * stream — see CameraStreamModel); defaults to the raw model when omitted. focalScale is a
 * diagnostic multiplier on the model's fx/fy (from the pose fitter's lens-scale estimate),
 * defaulting to 1.
 */
export const useImageCoordTranslator = (device: Device | undefined, imageWidth: number, imageHeight: number, cameraModel?: CameraStreamModel, focalScale?: number): ImageCoordTranslator => {
    const imgWidth = imageWidth > 0 ? imageWidth : TRANSLATOR_WIDTH;
    const imgHeight = imageHeight > 0 ? imageHeight : TRANSLATOR_HEIGHT;

    const homography = useMemo(() => {
        const src = device?.homographySourcePoints;
        const dst = device?.homographyDestinationPoints;
        if ((src?.length ?? 0) < 4 || (dst?.length ?? 0) < 4) return null;

        // Source points are camera pixels (640x480 basis); destination points are device-frame ground meters.
        const srcFlat = src!.slice(0, 4).flatMap((p) => [p.x, p.y]);
        const dstFlat = dst!.slice(0, 4).flatMap((p) => [p.x, p.y]);
        try {
            return new HomographyHelper(srcFlat, dstFlat);
        } catch (err) {
            console.error("Failed to build homography translator", err);
            return null;
        }
    }, [device?.homographySourcePoints, device?.homographyDestinationPoints]);

    const intrinsics = useMemo(() => {
        if (!device || homography) return null;

        const t = new IntrinsicsTranslator();
        const { pitchDeg, rollDeg } = intrinsicsAnglesForDevice(withTiltFallback(device));
        const returnHeight = device.fusionEstimatedReturnHeight ?? 0.8;

        t.calculateFromParameters(false, device.height ?? 2, pitchDeg, rollDeg, returnHeight, cameraModel);
        if (focalScale != null && focalScale !== 1) t.setFocalScale(focalScale);
        return t;
    }, [device, homography, cameraModel, focalScale]);

    const deviceTransform = useMemo(() => {
        return {
            posX: device?.positionX ?? 0,
            posY: device?.positionY ?? 0,
            headingDeg: device?.headingDegrees ?? 0,
        };
    }, [device?.positionX, device?.positionY, device?.headingDegrees]);

    const scaleRatio = useMemo(() => imgWidth / 20, [imgWidth]);

    const translatorToImageCoords = useCallback(
        (point: Vector2): Vector2 =>
            new Vector2({
                x: (point.x / TRANSLATOR_WIDTH) * imgWidth,
                y: (point.y / TRANSLATOR_HEIGHT) * imgHeight,
            }),
        [imgWidth, imgHeight],
    );

    const imageToTranslatorCoords = useCallback(
        (point: Vector2): Vector2 =>
            new Vector2({
                x: (point.x / imgWidth) * TRANSLATOR_WIDTH,
                y: (point.y / imgHeight) * TRANSLATOR_HEIGHT,
            }),
        [imgWidth, imgHeight],
    );

    /** Device-frame ground meters -> translator (640x480) pixels */
    const deviceGroundToTranslatorCoords = useCallback(
        (deviceGround: Vector3): Vector2 => {
            if (homography) {
                return homography.transformInverse(deviceGround.x, deviceGround.y);
            }
            return intrinsics!.groundToImage(deviceGround);
        },
        [homography, intrinsics],
    );

    /** Translator (640x480) pixels -> device-frame ground meters */
    const translatorCoordsToDeviceGround = useCallback(
        (coords: Vector2): Vector3 => {
            if (homography) {
                const ground = homography.transform(coords.x, coords.y);
                // Clamp runaway projections near the horizon, mirroring imageToGroundClamped.
                const range = Math.sqrt(ground.x * ground.x + ground.y * ground.y);
                if (Number.isFinite(range) && range > MAX_GROUND_RANGE_METERS) {
                    const scale = MAX_GROUND_RANGE_METERS / range;
                    return new Vector3({ x: ground.x * scale, y: ground.y * scale, z: 0 });
                }
                return new Vector3({ x: ground.x, y: ground.y, z: 0 });
            }
            return intrinsics!.imageToGroundClamped(coords, 0, MAX_GROUND_RANGE_METERS);
        },
        [homography, intrinsics],
    );

    const groundToImagePixels = useCallback(
        (facilityGround: Vector3): Vector2 => {
            if (!homography && !intrinsics) {
                return new Vector2({ x: imgWidth / 2, y: imgHeight / 2 });
            }
            const { posX, posY, headingDeg } = deviceTransform;
            const [devicePoint] = facilityToDevice([new Vector2({ x: facilityGround.x, y: facilityGround.y })], { x: posX, y: posY }, headingDeg);
            const translatorCoords = deviceGroundToTranslatorCoords(new Vector3({ x: devicePoint.x, y: devicePoint.y, z: facilityGround.z }));
            return translatorToImageCoords(translatorCoords);
        },
        [homography, intrinsics, deviceTransform, deviceGroundToTranslatorCoords, translatorToImageCoords, imgWidth, imgHeight],
    );

    const imagePixelsToGround = useCallback(
        (pixel: Vector2): Vector3 => {
            if (!homography && !intrinsics) {
                return new Vector3({ x: 0, y: 0, z: 0 });
            }
            const { posX, posY, headingDeg } = deviceTransform;
            const deviceGround = translatorCoordsToDeviceGround(imageToTranslatorCoords(pixel));
            const [facilityPoint] = deviceToFacility([new Vector2({ x: deviceGround.x, y: deviceGround.y })], { x: posX, y: posY }, headingDeg);
            return new Vector3({ x: facilityPoint.x, y: facilityPoint.y, z: deviceGround.z });
        },
        [homography, intrinsics, deviceTransform, translatorCoordsToDeviceGround, imageToTranslatorCoords],
    );

    return {
        groundToImagePixels,
        imagePixelsToGround,
        isCalibrated: !!homography || !!intrinsics,
        deviceHeadingDeg: deviceTransform.headingDeg,
        scaleRatio,
    };
};
